import { Fragment } from 'react';

import StatusBadge from 'src/components/atoms/StatusBadge';
import { cn } from 'src/components/ui/utils';

import { resolveAccent } from './accents';
import type { ResolvedSlot } from './slotSelection';

/**
 * One result card — shared by every list the palette renders: live search groups,
 * the "Best match" hit, and recents. Presentation only; the search hook and the
 * recents store both emit `PaletteCardData` with its slots already selected.
 *
 *   ┌────┬──────────────────────────────────────┬───────────┐
 *   │ ▧  │ SO-00612  SALES ORDER                 │ [Picking] │  ← identity line
 *   │icon│ CUST Sysco West   REF PO-4471         │           │  ← slot line (≤3)
 *   └────┴──────────────────────────────────────┴───────────┘
 *     ▲ accent resolved from `accentKey`
 *
 * The slot that matched the query leads its line, `<mark>`-highlighted from the
 * engine snippet (React text nodes, NEVER `dangerouslySetInnerHTML`). A slot
 * with no value drops itself AND its label — never a dangling "ITEMS ".
 */
export interface PaletteCardData {
  id: string;
  /** Accent identity (`config.accentKey`); a plain string so recents can round-trip it. */
  accentKey: string;
  /** Line-1 monospace identifier (SO-00612, a lot number, an account number…). */
  identifier?: string;
  /** Line-1 uppercase entity-type label (SALES ORDER, LOT, INVOICE…). */
  typeLabel: string;
  status?: string;
  /** Line-2 labelled detail slots, already selected + capped at 3 (matched slot first). */
  slots: ResolvedSlot[];
  route: string;
  /**
   * Owning collection alias. Carried so a persisted recent can be dropped when
   * the collection leaves the caller's entitlement (see `useRecentRecords`); the
   * row itself never renders it.
   */
  collectionKey: string;
}

interface ResultCardProps {
  data: PaletteCardData;
  active: boolean;
  onSelect: () => void;
  /** "Best match" gets the accent left-rail + tint. */
  emphasis?: boolean;
  /** `role="option"` id, referenced by the input's `aria-activedescendant`. */
  id?: string;
}

/** The card shell: accent icon box + the identity line over the slot line. */
export function ResultCard({ data, active, onSelect, emphasis = false, id }: ResultCardProps) {
  const accent = resolveAccent(data.accentKey);
  const Icon = accent.icon;

  return (
    <button
      type="button"
      // Virtual-cursor combobox: real focus never leaves the input, so the row is an ARIA option, not a tab stop. Pointer-down `preventDefault` keeps focus on the input; activation is `onClick` so right/middle-click do not navigate.
      id={id}
      role="option"
      aria-selected={active}
      tabIndex={-1}
      onPointerDown={(event) => event.preventDefault()}
      onClick={onSelect}
      data-active={active}
      className={cn(
        'flex h-[52px] w-full items-center gap-[12px] rounded-[10px] px-[10px] text-left',
        // Best match keeps the inset brand rail as its marker; reserve the darker
        // selected fill for the keyboard highlight so exactly one row reads active.
        emphasis && 'shadow-[inset_2px_0_0_var(--color-brand-6)]',
        active ? 'bg-brand-alpha-2' : emphasis ? 'bg-brand-alpha-1' : 'hover:bg-neutral-alpha-2'
      )}
    >
      <span className={cn('flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px]', accent.box)}>
        <Icon className={cn('h-[17px] w-[17px]', accent.fg)} aria-hidden />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <CardIdentityLine identifier={data.identifier} typeLabel={data.typeLabel} status={data.status} />
        <CardSlotLine slots={data.slots} />
      </span>
    </button>
  );
}

/** Line 1: mono identifier + small uppercase entity-type label + right-aligned status badge. */
function CardIdentityLine({
  identifier,
  typeLabel,
  status,
}: {
  identifier?: string;
  typeLabel: string;
  status?: string;
}) {
  return (
    <span className="flex min-w-0 items-center gap-[8px]">
      {identifier && (
        <span className="shrink-0 truncate text-15 font-semibold text-neutral-text [font-family:ui-monospace,SFMono-Regular,monospace]">
          {identifier}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate text-11 font-medium uppercase tracking-wider text-neutral-alpha-7">
        {typeLabel}
      </span>
      {status && (
        <span className="shrink-0">
          <StatusBadge status={status} size="small" />
        </span>
      )}
    </span>
  );
}

/** Line 2: up to 3 labelled slots on a single truncated line; nothing renders when empty. */
export function CardSlotLine({ slots }: { slots: ResolvedSlot[] }) {
  if (slots.length === 0) return null;
  return (
    <span className="flex min-w-0 items-center gap-[12px] truncate text-12 text-neutral-alpha-8">
      {slots.map((slot) => (
        <Slot key={slot.field} label={slot.label} value={slot.value} highlight={slot.highlight} />
      ))}
    </span>
  );
}

/** A single labelled slot: small uppercase label + its value. */
export function Slot({ label, value, highlight }: Pick<ResolvedSlot, 'label' | 'value' | 'highlight'>) {
  return (
    <span className="flex min-w-0 shrink items-center gap-[4px] truncate">
      <span className="shrink-0 text-10 font-semibold uppercase tracking-wide text-neutral-alpha-6">{label}</span>
      <span className="min-w-0 truncate text-neutral-alpha-9">
        <SlotValue value={value} highlight={highlight} />
      </span>
    </span>
  );
}

/** Renders a slot value: plain text, or `<mark>`-wrapped segments from a highlight snippet. */
function SlotValue({ value, highlight }: Pick<ResolvedSlot, 'value' | 'highlight'>) {
  if (!highlight || highlight.length === 0) return <>{value}</>;
  return (
    <>
      {highlight.map((segment, index) =>
        segment.marked ? (
          <mark key={index} className="rounded-[3px] bg-brand-alpha-3 px-[1px] text-inherit">
            {segment.text}
          </mark>
        ) : (
          <Fragment key={index}>{segment.text}</Fragment>
        )
      )}
    </>
  );
}
